import React from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import withStyles from 'isomorphic-style-loader/withStyles';
import numeri from 'numeri';
import s from './rogue_earth.css';
import utils from '../../core/utils';
import random from '../../core/random';

const maxDisplayScores = 5;
const rng = new random.Rng(random.randomSeed());
const xDim = 550;
const yDim = 550;
const pauseLineY = 200;
const svgId = 'rogue-earth-svg';
const gravity = 0.006;
const keyAcc = 0.65;
const earthMass = 1.0;
const powerUpMass = 1E-8;
const earthRad = 0.03;
const foeRad = 0.03;
const powerUpRad = 0.01;
const earthColor = [0, 85, 170];
const foeColor = [170, 0, 0];
const frameDt = 1 / 30.0;
const padding = 6;
const arrowKeyCodes = new Set([
  37,
  38,
  39,
  40,
]);
const trackKeyCodes = new Set([
  65,
  68,
  83,
  87,
  ...arrowKeyCodes,
]);
const anyKeyCodes = new Set([
  ...arrowKeyCodes,
  13, // enter
  32, //space
]);
for (let i = 65; i < 91; i++) {
  // letters
  anyKeyCodes.add(i);
}
for (let i = 48; i < 58; i++) {
  // numbers
  anyKeyCodes.add(i);
}
const keyPressState = {};
const powerUpEffects = [
  {
    name: 'shield',
    duration: 6,
    color: [127, 127, 127],
  },
  {
    name: 'shrink',
    duration: 8,
    color: [0, 150, 100],
    apply: (rogueEarth) => {
      rogueEarth.rad.set([0], earthRad * 0.5);
    },
    unapply: (rogueEarth) => {
      rogueEarth.rad.set([0], earthRad);
    },
  },
  {
    name: 'antigravity',
    duration: 8,
    color: [50, 0, 100],
    apply: (rogueEarth) => {
      rogueEarth.mass.set([0], -earthMass);
    },
    unapply: (rogueEarth) => {
      rogueEarth.mass.set([0], earthMass);
    },
  },
  {
    name: 'turbo',
    duration: 8,
    color: [220, 230, 0],
  },
  {
    name: 'slomo',
    duration: 4,
    color: [240, 150, 0],
  },
];
const powerUpNames = powerUpEffects.map((effect) => effect.name);
const powerUpEffectMap = {};
powerUpEffects.forEach((effect) => {
  powerUpEffectMap[effect.name] = effect;
});
// config to list
const topScores = {};

const formatT = (t) => {
  return t.toFixed(2);
};
const formatColor = (colorList) => {
  return `rgb(${colorList.map(Math.floor).join(',')})`;
};

class RogueEarth extends React.Component {
  static propTypes = {
    settings: PropTypes.any,
  }

  componentDidMount() {
    if (!this.svg) {
      this.svg = d3.select(`#${svgId}`);
    }

    this.updatePromise = Promise.resolve();
    Array.from(trackKeyCodes).forEach((keyCode) => {
      keyPressState[keyCode] = 0;
    });

    this.svg
      .on('keydown', () => {
        const { keyCode } = d3.event;
        this.handleKeyEvent(keyCode, 1);
      })
      .on('keyup', () => {
        const { keyCode } = d3.event;
        this.handleKeyEvent(keyCode, 0);
      });
    this.newGame();
    document.getElementById(svgId).focus();
  }

  shouldComponentUpdate() {
    return false;
  }

  getConfigKey() {
    const { settings } = this;
    const { numFoes, foeMass, powerUpInterval } = settings;
    return `${numFoes}-${foeMass}-${powerUpInterval}`;
  }

  getAcc() {
    const { pos, rad, mass } = this;
    const disp = pos
      .broadcastOn(0)
      .minus(pos.broadcastOn(1));

    const dist = disp
      .norm({ axes: [2] });

    const minDist = rad
      .broadcastOn(0)
      .plus(rad.broadcastOn(1));

    const safeD = dist
      .elemwiseBinaryOp(minDist, Math.max);

    const acc = dist
      .elemwiseBinaryOp(
        mass.broadcastOn(0),
        (d, m) => d > 0 ? gravity * m / d : 0,
      )
      .elemwiseBinaryOpInPlace(
        safeD,
        (accTerm, d) => accTerm / d / d,
      )
      .broadcastOn(2)
      .elemwiseBinaryOp(
        disp,
        (accTerm, di) => accTerm * di,
      )
      .sum({ axes: [1] });

    const modKeyAcc = this.powerUpDurs.turbo > 0 ? 2 * keyAcc : keyAcc;
    const keyLeft = keyPressState[37] || keyPressState[65];
    const keyUp = keyPressState[38] || keyPressState[87];
    const keyRight = keyPressState[39] || keyPressState[68];
    const keyDown = keyPressState[40] || keyPressState[83];
    const keyX = keyRight - keyLeft;
    const keyY = keyDown - keyUp;
    const div = Math.sqrt(keyX * keyX + keyY * keyY + 1E-16);
    acc.update([0, 0], (a) => a + modKeyAcc * keyX / div);
    acc.update([0, 1], (a) => a + modKeyAcc * keyY / div);
    return acc;
  }

  handleKeyEvent(keyCode, value) {
    if (anyKeyCodes.has(keyCode)) {
      d3.event.preventDefault();
    } else {
      return;
    }

    if (trackKeyCodes.has(keyCode)) {
      keyPressState[keyCode] = value;
    }

    if (value === 1) {
      if (keyCode === 78) { // n
        this.clear();
        this.newGame();
      } else if (keyCode === 80 && this.running) { // p
        this.stop();
      } else if (!this.running) {
        this.start();
      }
    }
  }

  clear() {
    this.running = false;
    clearInterval(this.intervalId);
    [
      'mainG',
      'textG',
      'pauseLayer',
      'timeText',
    ].forEach((key) => {
      if (this[key]) {
        if (this[key].remove) {
          this[key].remove();
        }
        delete this[key];
      }
    });
  }

  newGame() {
    this.lost = false;
    this.running = false;
    this.frame = 0;
    this.frameT = 0;
    this.settings = Object.assign({}, this.props.settings);
    const { numFoes, foeMass } = this.settings;
    delete this.finalT;

    this.types = [{ category: 'earth', color: earthColor.slice() }];
    const pos = [[0.5, 0.5]];
    const mass = [earthMass];
    const rad = [earthRad];

    for (let i = 0; i < numFoes; i++) {
      let valid = false;
      while (!valid) {
        const p = [1, Math.random()];
        if (Math.random() < 0.5) {
          p[0] = 0;
        }
        if (Math.random() < 0.5) {
          const temp = p[1];
          p[1] = p[0];
          p[0] = temp;
        }
        valid = true;
        for (let j = 1; j < i + 1; j++) {
          const dx = p[0] - pos[j][0];
          const dy = p[1] - pos[j][1];
          const r = Math.sqrt(dx * dx + dy * dy);
          if (r <= 2 * foeRad) {
            valid = false;
          }
        }
        if (valid) {
          pos.push(p);
        }
      }
      this.types.push({ category: 'foe', color: foeColor });
      mass.push(foeMass);
      rad.push(foeRad);
    }
    this.pos = numeri.fromNested(pos);
    this.vel = numeri.zeros(this.pos.shape);
    this.mass = numeri.vector(mass);
    this.rad = numeri.vector(rad);
    this.n = this.types.length;
    this.powerUpDurs = {};
    powerUpNames.forEach((name) => {
      this.powerUpDurs[name] = 0;
    });

    this.textG = this.svg
      .append('g');
    this.timeText = this.textG
      .append('text')
      .attr('class', s.info)
      .attr('x', xDim - padding)
      .attr('y', padding)
      .text(formatT(0));

    this.drawUpdate();
    this.drawPauseLayer();
  }

  start() {
    this.running = true;
    this.pauseLayer.remove();
    delete this.pauseLayer;
    this.intervalId = setInterval(
      () => {
        if (this.computing) {
          console.log('warning: skipped a frame because still computing');
          return;
        }
        this.computing = true;
        this.updatePromise = this.update().then(() => {
          this.computing = false;
        });
      },
      1000 * frameDt,
    );
  }

  stop() {
    if (!this.lost) {
      this.running = false;
      clearInterval(this.intervalId);
      this.updatePromise.then(() => {
        this.drawPauseLayer();
      });
    }
  }

  lose() {
    if (!this.lost) {
      this.lost = true;
      clearInterval(this.intervalId);
      this.drawPauseLayer({
        lose: true,
      });
    }
  }

  calcIterMeta(dt, fullDt) {
    const { vel, pos, n } = this;
    let collisions = [];
    let iterDt = fullDt - dt;

    const collisionAt = (aDt, collision) => {
      const effDt = Math.max(aDt, 0);
      if (effDt < 0) {
        throw new Error(`negative time, bad ${effDt}`);
      }
      if (effDt === iterDt) {
        collisions.push(collision);
      } else if (effDt < iterDt) {
        iterDt = effDt;
        collisions = [collision];
      }
    };

    for (let i = 0; i < n; i++) {
      const rad = this.rad.get(i);
      const px = pos.get(i, 0);
      const py = pos.get(i, 1);
      const vx = vel.get(i, 0);
      const vy = vel.get(i, 1);

      if (vx < 0 && px + vx * iterDt <= rad) {
        collisionAt(-(px - rad) / vx, { i, left: true });
      } else if (vx > 0 && px + vx * iterDt >= 1 - rad) {
        collisionAt((1 - rad - px) / vx, { i, right: true });
      }

      if (vy < 0 && py + vy * iterDt <= rad) {
        collisionAt(-(py - rad) / vy, { i, top: true });
      } else if (vy > 0 && py + vy * iterDt >= 1 - rad) {
        collisionAt((1 - rad - py) / vy, { i, bottom: true });
      }

      for (let j = 0; j < i; j++) {
        const r = rad + this.rad.get(j);
        const dvx = vx - vel.get(j, 0);
        const dvy = vy - vel.get(j, 1);
        const dpx = px - pos.get(j, 0);
        const dpy = py - pos.get(j, 1);
        if (dpx * dpx + dpy * dpy <= r * r) {
          continue;
        }
        const a = dvx * dvx + dvy * dvy;
        const b = 2 * (dpx * dvx + dpy * dvy);
        const c = dpx * dpx + dpy * dpy - r * r;
        const desc = b * b - 4 * a * c;

        if (desc < 0) {
          continue;
        }

        const collideDts = [1, -1]
          .map((mult) => {
            return (-b + mult * Math.sqrt(desc)) / (2 * a);
          })
          .filter((sol) => {
            return sol >= 0;
          });
        if (collideDts.length < 2) {
          continue;
        }
        collisionAt(Math.min(...collideDts), { i, j });
      }
    }

    return {
      collisions,
      iterDt,
    };
  }

  calcIter(iterMeta, dt) {
    const { vel, pos, mass, frameT, powerUpDurs, types, settings } = this;
    const acc = this.getAcc();
    const { iterDt, collisions } = iterMeta;
    const oldTime = frameT + dt;
    const newTime = oldTime + iterDt;

    powerUpEffects.forEach((effect) => {
      const { name, unapply } = effect;
      if (powerUpDurs[name] > 0) {
        const reduced = powerUpDurs[name] - iterDt;
        if (reduced <= 0) {
          powerUpDurs[name] = 0;
          if (unapply) {
            unapply(this);
          }
        } else {
          powerUpDurs[name] = reduced;
        }
      }
    });
    const objectsToDelete = [];

    pos.elemwiseBinaryOpInPlace(vel, (p, v) => p + iterDt * v);

    collisions.forEach((collision) => {
      const { i, j } = collision;
      if (collision.left || collision.right) {
        vel.update([i, 0], (v) => -v);
      } else if (collision.top || collision.bottom) {
        vel.update([i, 1], (v) => -v);
      } else if (j === 0 && types[i].category === 'power') {
        // collision with you and power up
        const t = types[i];
        powerUpDurs[t.name] += t.duration;
        if (t.apply) {
          t.apply(this);
        }
        objectsToDelete.push(i);
      } else if (j === 0 && !powerUpDurs.shield) {
        // fatal collision with you and foe
        this.finalT = newTime;
        this.lose();
      } else {
        // regular collision
        const m0 = mass.get(i);
        const m1 = mass.get(j);
        const disp = pos.slice(i).minus(pos.slice(j));
        const r2 = disp.map((x) => x * x).sum();
        const r = Math.sqrt(r2);
        const vec = disp.div(r);
        const v0 = vel.slice(i);
        const v1 = vel.slice(j);
        const impulse = -2 * m0 * m1 * numeri.dot(v0.minus(v1), vec) / (m0 + m1);
        v0.elemwiseBinaryOpInPlace(vec, (v, d) => v + impulse * d / m0);
        v1.elemwiseBinaryOpInPlace(vec, (v, d) => v - impulse * d / m1);
      }
    });

    vel.elemwiseBinaryOpInPlace(acc, (v, a) => v + iterDt * a);

    const powerUpProps = powerUpEffects.map((effect) => {
      const { name, duration } = effect;
      return Math.min(1, powerUpDurs[name] / duration);
    });
    const powerUpSum = numeri.vector(powerUpProps).sum();
    const earthType = types[0];
    earthType.color = earthColor.map((c, i) => {
      let res = c * Math.max(0, 1 - powerUpSum);
      powerUpEffects.forEach((effect, idx) => {
        res += effect.color[i] * powerUpProps[idx] /
          Math.max(1, powerUpSum);
      });
      return res;
    });

    objectsToDelete.forEach((object) => {
      this.deleteObject(object);
    });
    if (Math.floor(newTime / settings.powerUpInterval) >
      Math.floor(oldTime / settings.powerUpInterval)) {
      this.addPowerUp();
    }
  }

  calcStep() {
    let dt = 0;
    let iter = 0;
    const fullDt = this.powerUpDurs.slomo > 0 ? frameDt / 2 : frameDt;
    while (dt < fullDt && !this.lost) {
      const iterMeta = this.calcIterMeta(dt, fullDt);
      if (iter > 10 && iterMeta.iterDt <= 0) {
        throw new Error(`On iteration ${iter} and iterDt=${iterMeta.iterDt}`);
      }
      this.calcIter(iterMeta, dt);
      dt += iterMeta.iterDt;
      iter++;
    }
    this.frame ++;
    this.frameT += fullDt;
  }

  addToTensor(key, row) {
    this[key] = numeri.concat(
      [
        this[key],
        numeri.fromNested([row]),
      ],
      0,
    );
  }

  addPowerUp() {
    const effect = rng.singleChoice(powerUpEffects);
    let p;
    let valid = false;
    while (!valid) {
      valid = true;
      p = [Math.random(), Math.random()];
      for (let i = 0; i < this.n; i++) {
        const dx = p[0] - this.pos.get(i, 0);
        const dy = p[1] - this.pos.get(i, 0);
        const r = Math.sqrt(dx * dx + dy * dy);
        if (r <= powerUpRad + this.rad.get(i)) {
          valid = false;
        }
      }
    }

    this.addObject(
      p,
      [0, 0],
      powerUpMass,
      powerUpRad,
      Object.assign({ category: 'power' }, effect),
    );
  }

  addFoe() {
    this.addObject(
      [0, 0],
      [0, 0],
      this.settings.foeMass,
      foeRad,
      { category: 'foe', color: foeColor },
    );
  }

  addObject(p, v, m, r, t) {
    this.addToTensor('pos', p);
    this.addToTensor('vel', v);
    this.addToTensor('mass', m);
    this.addToTensor('rad', r);
    this.types.push(t);
    this.n ++;
  }

  deleteFromTensor(key, i) {
    this[key] = numeri.concat(
      [
        this[key].slice([0, i]),
        this[key].slice([i + 1, this[key].shape[0]]),
      ],
      0,
    );
  }

  deleteObject(i) {
    ['pos', 'vel', 'mass', 'rad'].forEach((key) => {
      this.deleteFromTensor(key, i);
    });
    this.types.splice(i, 1);
    this.n --;
  }

  async update() {
    this.calcStep();
    this.drawUpdate();
    // this.debugging();
  }

  debugging() {
    const mass1 = this.mass.broadcastOn(1);
    const momentum = this.vel
      .elemwiseBinaryOp(mass1, (v, m) => m * v)
      .sum({ axes: [0] });
    const kineticEnergy = this.vel
      .elemwiseBinaryOp(mass1, (v, m) => 0.5 * m * v * v)
      .sum();
    const dist2s = this.pos
      .broadcastOn(0)
      .minus(this.pos.broadcastOn(1))
      .map((x) => x * x)
      .sum({ axes: [2] });
    const potentialEnergy = -this.mass
      .broadcastOn(0)
      .times(mass1)
      .elemwiseBinaryOpInPlace(dist2s, (m0m1, dist2) => {
        return dist2 === 0 ? 0 : 0.5 * m0m1 * gravity / Math.sqrt(dist2);
      })
      .sum();
    const energy = potentialEnergy + kineticEnergy;
    console.log([
      `P ${potentialEnergy.toFixed(5)}`,
      `K ${kineticEnergy.toFixed(5)}`,
      `T ${energy.toFixed(5)}`,
      `M ${momentum.toNested()}`,
    ].join(' '));
  }

  drawUpdate() {
    if (this.mainG) {
      this.mainG.remove();
    }
    this.mainG = this.svg
      .append('g');

    const { n } = this;
    for (let i = 0; i < n; i++) {
      const t = this.types[i];
      this.mainG
        .append('circle')
        .attr('fill', formatColor(t.color))
        .attr('cx', this.pos.get(i, 0) * xDim)
        .attr('cy', this.pos.get(i, 1) * yDim)
        .attr('r', this.rad.get(i) * xDim);
    }

    if (this.powerUpG) {
      this.powerUpG.remove();
      delete this.powerUpG;
    }
    const nonzeroDurs = Object.entries(this.powerUpDurs)
      .filter((entry) => entry[1] > 0);
    if (nonzeroDurs.length > 0) {
      this.powerUpG = this.textG
        .append('g');
      nonzeroDurs.forEach((entry, idx) => {
        const color = formatColor(powerUpEffectMap[entry[0]].color);
        const text = `${entry[0]}: ${formatT(entry[1])}`;
        this.powerUpG
          .append('text')
          .attr('x', padding)
          .attr('y', padding + idx * 20)
          .attr('fill', color)
          .attr('class', s.powertext)
          .text(text);
      });
    }
    this.timeText
      .text(formatT(this.finalT || this.frameT));
    this.textG
      .raise();
    if (this.pauseLayer) {
      this.pauseLayer.raise();
    }
  }

  drawPauseLayer(options) {
    const { lose } = utils.defaults(options, {
      lose: false,
    });

    this.pauseLayer = this.svg
      .append('g');
    this.pauseLayer
      .append('rect')
      .attr('width', xDim)
      .attr('height', yDim)
      .attr('class', s.pause);

    const texts = (() => {
      if (lose) {
        const configKey = this.getConfigKey();
        topScores[configKey] = topScores[configKey] || [];
        const topScoreList = topScores[configKey];
        topScoreList.push(this.finalT);
        topScoreList.sort((a, b) => b - a);
        const topScoreLines = topScoreList
          .slice(0, maxDisplayScores)
          .map((score) => formatT(score));
        return [
          'press n for new game',
          `you survived ${formatT(this.finalT)} seconds`,
          'high scores:',
        ].concat(topScoreLines);
      }
      return [
        'press n for new game',
        'or any other key to unpause',
      ];
    })();
    texts.forEach((text, idx) => {
      this.pauseLayer
        .append('text')
        .attr('x', xDim / 2)
        .attr('y', pauseLineY + 30 * idx)
        .attr('class', s.pausetext)
        .text(text);
    });
  }

  render() {
    return (
      <div className={s.container}>
        <svg width={xDim} height={yDim} id={svgId} tabIndex="0">
          <rect width={xDim} height={yDim} fill="black" />
        </svg>
      </div>
    );
  }
}

export default withStyles(s)(RogueEarth);



// WEBPACK FOOTER //
// src/components/RogueEarthControls/RogueEarth.js